import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check } from 'lucide-react'
import { useDashboardStore } from '../../store/dashboardStore'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'

// ── Шаги тура ────────────────────────────────────────────────────────────────
// target — селектор элемента с data-hint-меткой. tab — вкладка, subTab —
// под-вкладка «Боты», modal — модалка (сейчас туру не нужна: см. ниже).
// skipIf(state) — true → шаг пропускается (у юзера уже настроено / он админ).
// Номера в ключах переводов (hintStepN) — исторические id, они НЕ означают
// порядок показа: порядок задаётся только этим массивом.
const STEPS = [
  // ПЕРВЫМ делом — где лежит полная инструкция. Юзер должен узнать это до тура:
  // подсказки короткие, а подробности он ищет потом и должен знать, где искать.
  //
  // Раньше этот шаг сам открывал окно настроек (modal: 'settings'), и
  // приложение у юзера «стартовало с настроек» вместо главной. Особенно
  // заметно после смены домена: localStorage привязан к origin, флаг
  // hintsDone остался на старом домене, тур запускался заново у ВСЕХ старых
  // юзеров. Теперь тур только подсвечивает шестерёнку в шапке (текст шага и
  // так говорит «Настройки (шестерёнка) → Инструкция»), а открывает её юзер
  // сам. Открытие приложения всегда начинается с главной.
  { id: 'instruction', tab: 'dashboard', target: '[data-hint="settings-btn"]', title: 'hintStep8Title', text: 'hintStep8Text', placement: 'bottom' },
  // ГЛАВНОЕ действие для новичка — создание ПРОБНОГО бота: для нового юзера
  // «Добавить бота» создаёт бесплатного виртуального бота сразу, без модалки
  // (без API-ключей, 2 сделки на виртуальные деньги). Остальные шаги тура
  // убраны по задаче — подробности юзер найдёт в Настройки → Инструкция.
  { id: 'add-bot',    tab: 'bots',      target: '[data-hint="add-bot"]',            title: 'hintStep4Title', text: 'hintStep4Text', placement: 'bottom',
    skipIf: s => (s.userBots && s.userBots.length > 0) },
  { id: 'done',       target: null,     title: 'hintDoneTitle', text: 'hintDoneText', placement: 'center' },
]

// Снапшот состояния для «умного пропуска» шагов. Читается лениво (getState),
// поэтому к моменту клика «Дальше» данные о ботах уже загружены.
const snapshot = () => {
  const s = useDashboardStore.getState()
  return {
    userBots: s.userBots,
  }
}

const isVisible = (step) => !step.skipIf || !step.skipIf(snapshot())

// Индекс следующего НЕ пропущенного шага, начиная с fromIdx; -1 = конец тура.
const nextVisible = (fromIdx) => {
  for (let i = fromIdx; i < STEPS.length; i++) {
    if (isVisible(STEPS[i])) return i
  }
  return -1
}

// env(safe-area-inset-*) из JS не прочитать — измеряем скрытым пробником один
// раз за загрузку (в Mini App инсеты за время тура не меняются). Нужны, чтобы
// пузырь не заезжал под шапку приложения и под нижнее меню.
let insetsCache = null
const insets = () => {
  if (insetsCache) return insetsCache
  insetsCache = { top: 0, bottom: 0 }
  if (typeof document === 'undefined' || !document.body) return insetsCache
  const probe = document.createElement('div')
  probe.style.cssText = 'position:fixed;left:0;top:0;width:0;visibility:hidden;pointer-events:none;'
    + 'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)'
  document.body.appendChild(probe)
  const cs = getComputedStyle(probe)
  insetsCache = { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 }
  probe.remove()
  return insetsCache
}

// Пузырь-подсказка выводится в document.body поверх всего (z-index 12000 —
// выше модалок 10000 и окон оплаты 11000), чтобы спотлайт не прятался
// за нижним меню и любой модалкой.
export default function HintsTour() {
  const t = useT()
  const haptic = useHaptic()
  const finishHintsTour = useDashboardStore(s => s.finishHintsTour)
  const setHintsEnabled = useDashboardStore(s => s.setHintsEnabled)
  const setActiveTab     = useDashboardStore(s => s.setActiveTab)
  const setBotsMainTab   = useDashboardStore(s => s.setBotsMainTab)
  const setActiveModal   = useDashboardStore(s => s.setActiveModal)

  const [pointer, setPointer] = useState(() => nextVisible(0))
  const [rect, setRect] = useState(null)    // bounding rect целевого элемента
  const [ready, setReady] = useState(false)
  const bubbleRef = useRef(null)
  const [bubbleH, setBubbleH] = useState(0) // реальная высота пузыря (для позиции)

  const step = pointer >= 0 ? STEPS[pointer] : null
  const totalVisible = STEPS.filter(isVisible).length
  const position = step
    ? STEPS.slice(0, pointer).filter(isVisible).length + 1
    : 0

  // Переключение окружения шага: вкладка/под-вкладка/модалка + поиск цели.
  useEffect(() => {
    if (!step) { finish(); return }
    let cancelled = false

    if (step.modal) setActiveModal(step.modal)
    else setActiveModal(null)
    if (step.tab) setActiveTab(step.tab)
    if (step.subTab) setBotsMainTab(step.subTab)

    setReady(false)
    setRect(null)

    const sel = step.target
    if (!sel) { setReady(true); return } // финальный шаг — без цели

    const measure = () => {
      if (cancelled) return
      const el = document.querySelector(sel)
      if (!el) return
      const r = el.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
      setReady(true)
    }

    const attempt = () => {
      if (cancelled) return
      const el = document.querySelector(sel)
      if (!el) {
        // Цель ещё не смонтировалась (переключение вкладки, fade) — ждём.
        if (tries < 60) { tries++; setTimeout(attempt, 100); return }
        // Так и не появилась — пропускаем шаг, чтобы тур не завис.
        setPointer(nextVisible(pointer + 1))
        return
      }
      try { el.scrollIntoView({ block: 'center', behavior: 'auto' }) } catch {}
      requestAnimationFrame(() => requestAnimationFrame(measure))
      setTimeout(measure, 500)
    }

    const onScrollResize = () => measure()
    window.addEventListener('scroll', onScrollResize, true)
    window.addEventListener('resize', onScrollResize)

    let tries = 0
    const timer = setTimeout(attempt, 250)

    return () => {
      cancelled = true
      window.removeEventListener('scroll', onScrollResize, true)
      window.removeEventListener('resize', onScrollResize)
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointer])

  // Высота пузыря зависит от длины текста шага, а позиция считается от неё.
  // Замер в useLayoutEffect — до отрисовки, поэтому видимого прыжка нет.
  useLayoutEffect(() => {
    const el = bubbleRef.current
    if (!el) return
    const h = el.getBoundingClientRect().height
    setBubbleH(prev => (Math.abs(prev - h) > 1 ? h : prev))
  }, [pointer, rect])

  const finish = () => {
    haptic.success()
    setActiveModal(null)
    finishHintsTour()
  }

  const next = () => {
    haptic.light()
    const idx = nextVisible(pointer + 1)
    if (idx < 0) finish()
    else setPointer(idx)
  }

  const disable = () => {
    haptic.medium()
    setActiveModal(null)
    setHintsEnabled(false)
  }

  if (!step) return null
  const isLast = position >= totalVisible

  // ── Позиция пузыря относительно цели ─────────────────────────────────────
  // ГЛАВНОЕ ПРАВИЛО: пузырь не закрывает цель — иначе юзер не видит того, о чём
  // подсказка (так строка «Инструкция» в настройках оказывалась под пузырём:
  // она первая в модалке, её rect.top мал, и пузырь прижимался к верху экрана
  // прямо поверх неё). Поэтому берём РЕАЛЬНУЮ высоту пузыря и ставим его на ту
  // сторону цели, где он влезает целиком: желаемая сторона (step.placement) —
  // приоритет, при нехватке места уходим на противоположную.
  // Заодно ушёл старый баг: setRect не пишет bottom, поэтому сравнение
  // (vh - rect.bottom) давало NaN и КАЖДЫЙ нижний пузырь прижимался к низу
  // экрана вместо позиции под целью.
  const vh = (typeof window !== 'undefined') ? window.innerHeight : 800
  const GAP = 12                              // зазор между целью и пузырём
  const EDGE_TOP = 70 + insets().top          // ниже шапки приложения
  const EDGE_BOTTOM = 84 + insets().bottom    // выше нижнего меню

  let bubblePos = null
  if (step.placement !== 'center') {
    const h = bubbleH || 180                  // до первого замера — оценка
    if (!rect) {
      // Цель не нашлась — пузырь по центру, закрывать нечего.
      bubblePos = { top: Math.max(EDGE_TOP, (vh - h) / 2) }
    } else {
      const spaceAbove = (rect.top - GAP) - EDGE_TOP
      const spaceBelow = vh - EDGE_BOTTOM - (rect.top + rect.height + GAP)
      const wantAbove = step.placement === 'top'
      const above = wantAbove
        ? (spaceAbove >= h || spaceAbove >= spaceBelow)
        : (spaceBelow < h && (spaceAbove >= h || spaceAbove > spaceBelow))
      let top = above ? rect.top - GAP - h : rect.top + rect.height + GAP
      // Не вылезаем за края экрана (в самом тесном случае — просто у края).
      top = Math.max(EDGE_TOP, Math.min(top, vh - EDGE_BOTTOM - h))
      bubblePos = { top }
    }
  }

  const bubbleShell = {
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(100vw - 32px, 448px)',
    zIndex: 12001,
    ...bubblePos,
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 12000 }}>
      {/* Слой-перехватчик кликов. Затемняет только когда спотлайта нет
          (финальный шаг или цель не нашлась): при подсвеченной цели затемнение
          рисует box-shadow «0 0 0 9999px» у .hint-spot, и второй слой поверх
          делал экран почти чёрным — юзер не видел интерфейс приложения. */}
      <div style={{
        position: 'absolute', inset: 0,
        background: rect ? 'transparent' : 'var(--hint-dim)',
      }} />

      {/* Спотлайт: прозрачная «дырка» вокруг цели + зелёное пульсирующее кольцо */}
      {rect && (
        <div
          className="hint-spot"
          style={{
            position: 'fixed',
            top: rect.top, left: rect.left,
            width: rect.width, height: rect.height,
            borderRadius: 14,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Центрированная карточка финального шага */}
      {step.placement === 'center' ? (
        <div className="hint-bubble" style={{
          ...bubbleShell,
          top: '50%', bottom: 'auto',
          transform: 'translate(-50%, -50%)',
          background: '#1C1C1E', border: '1px solid rgba(52,211,153,0.25)',
          borderRadius: 20, padding: '24px', textAlign: 'center',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(52,211,153,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 12px',
          }}>
            <Check size={28} color="#34D399" />
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{t[step.title]}</div>
          <p style={{ fontSize: 13.5, color: '#8E8E93', lineHeight: 1.55, margin: '0 0 16px' }}>{t[step.text]}</p>
          <button
            onClick={finish}
            style={{
              width: '100%', padding: '13px', borderRadius: 12,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: '#fff', fontSize: 15, fontWeight: 700,
              border: 'none', cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t.hintDoneBtn}
          </button>
        </div>
      ) : (
        <div ref={bubbleRef} className="hint-bubble" style={{
          ...bubbleShell,
          background: '#1C1C1E', border: '1px solid rgba(52,211,153,0.25)',
          borderRadius: 16, padding: '14px 16px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: '#8E8E93', fontWeight: 600, letterSpacing: '0.5px' }}>
              {position} / {totalVisible}
            </span>
            <button
              onClick={finish}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#636366', fontSize: 12, padding: '4px 6px', WebkitTapHighlightColor: 'transparent' }}
            >
              {t.hintSkip}
            </button>
          </div>

          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{t[step.title]}</div>
          <p style={{ fontSize: 13, color: '#8E8E93', lineHeight: 1.55, margin: '0 0 12px' }}>{t[step.text]}</p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <button
              onClick={disable}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#636366', fontSize: 12, padding: '8px 6px', WebkitTapHighlightColor: 'transparent' }}
            >
              {t.hintDisable}
            </button>
            <button
              onClick={isLast ? finish : next}
              style={{
                padding: '11px 22px', borderRadius: 12,
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#fff', fontSize: 14, fontWeight: 700,
                border: 'none', cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {isLast ? t.hintDoneBtn : t.hintNext}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  )
}
